import { useCallback, useEffect, useState } from 'react'
import {
  adminKeyStore,
  adminSessionStore,
  errorMessage,
  listLessons,
} from '../../services/admin'
import { STATUS_FA, STATUS_CLS, STATUS_ORDER } from './constants'
import { ChangePassword } from './components/ChangePassword'
import { KeyEntry } from './components/KeyEntry'
import { TABS } from './tabs'

// ─── Admin panel shell ────────────────────────────────────────────────────────

/**
 * Login gate, header and tab strip — everything that is not a tab (X1). The
 * tabs come from `TABS`; each gets the same props bag and reads what it
 * needs, so adding a tab is one row in tabs.js and one file, and this shell
 * does not change.
 */
function AdminPanel({ initialLessons, onLogout }) {
  const [lessons, setLessons] = useState(initialLessons)
  const [tab, setTab] = useState('lessons') // 'lessons' | 'calendar' | 'attempts' | 'notifications'
  const [view, setView] = useState('list') // 'list' | 'create' | 'auto-draft' (only for lessons tab)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  // «تغییر رمز» is for a named admin's own session; the shared API key has no account to change.
  const [changingPassword, setChangingPassword] = useState(false)
  const named = Boolean(adminSessionStore.access)

  const reload = useCallback(async () => {
    setLoading(true); setErr('')
    try { setLessons(await listLessons()) }
    catch (ex) { setErr(errorMessage(ex)) }
    finally { setLoading(false) }
  }, [])

  function mergeLessonUpdate(updated) {
    setLessons(ls => ls.map(l => l.id === updated.id ? updated : l))
  }

  const counts = lessons.reduce((acc, l) => { acc[l.status] = (acc[l.status] ?? 0) + 1; return acc }, {})

  const sorted = [...lessons].sort(
    (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.displayOrder - b.displayOrder
  )

  const ActiveTab = TABS.find(t => t.id === tab).Component

  return (
    <div data-admin className="min-h-dvh bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-4 py-3">
          <span className="font-bold text-slate-800">🦜 پنل ادمین</span>

          {/* Tabs */}
          <nav className="order-last -mx-4 flex w-[calc(100%+2rem)] items-center gap-1 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:order-none sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0">
            {TABS.map(({ id, label }) => (
              <button key={id} onClick={() => { setTab(id); setView('list') }}
                className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm transition-colors ${tab === id ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-500 hover:bg-slate-100'}`}>
                {label}
              </button>
            ))}
          </nav>

          {/* Status counts */}
          <div className="hidden items-center gap-2 sm:flex">
            {Object.entries(counts).map(([s, n]) => (
              <span key={s} className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[s]}`}>
                {n} {STATUS_FA[s]}
              </span>
            ))}
          </div>

          <div className="ms-auto flex flex-wrap items-center justify-end gap-2">
            <button onClick={reload} disabled={loading}
              className="rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50">
              {loading ? '…' : '↻'}
            </button>
            {tab === 'lessons' && view === 'list' && (
              <>
                <button onClick={() => setView('auto-draft')}
                  className="rounded-lg border border-brand-300 px-3 py-1.5 text-sm font-semibold text-brand-700 hover:bg-brand-50">
                  + از ویدیو/صدا
                </button>
                <button onClick={() => setView('create')}
                  className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700">
                  + درس جدید
                </button>
              </>
            )}
            {tab === 'lessons' && (view === 'create' || view === 'auto-draft') && (
              <button onClick={() => setView('list')}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                ← بازگشت
              </button>
            )}
            {named && (
              <button onClick={() => setChangingPassword(true)}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">
                تغییر رمز
              </button>
            )}
            <button onClick={onLogout}
              className="rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:bg-slate-100">
              خروج
            </button>
          </div>
        </div>
      </header>

      {changingPassword && <ChangePassword onClose={() => setChangingPassword(false)} />}

      <main className="mx-auto max-w-5xl space-y-3 px-3 py-4 sm:px-4 sm:py-6">
        {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}

        {/* The active tab. The bag is the same for every tab; the lessons
            tab is the one that uses `view`, `onCreated` and `onCancel`. */}
        <ActiveTab
          lessons={lessons}
          sorted={sorted}
          loading={loading}
          view={view}
          onUpdate={mergeLessonUpdate}
          onCreated={l => { setLessons(ls => [l, ...ls]); setView('list') }}
          onCancel={() => setView('list')}
        />
      </main>
    </div>
  )
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function Admin() {
  const [lessons, setLessons] = useState(null)

  useEffect(() => {
    if (!adminKeyStore.get() && !adminSessionStore.access) return
    listLessons().then(setLessons).catch(() => { adminKeyStore.clear(); adminSessionStore.clear() })
  }, [])

  if (lessons === null) return <KeyEntry onKey={setLessons} />

  return <AdminPanel initialLessons={lessons}
    onLogout={() => { adminKeyStore.clear(); adminSessionStore.clear(); setLessons(null) }} />
}
